import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import {
  Plus, Trash, FloppyDisk, Package, Truck, Hammer, ShoppingBag, Globe,
  Flag, House, MapPin, Receipt, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import DangerConfirm from "../components/DangerConfirm";

const SHIPPING_ZONES = [
  { key: "IT",       label: "Italia",                  icon: Flag,    flag: "🇮🇹" },
  { key: "EU_CONT",  label: "Europa Continentale",     icon: Globe,   flag: "🇪🇺" },
  { key: "EU_NORTH", label: "Nord Europa",             icon: Globe,   flag: "🇸🇪" },
  { key: "UK_CH",    label: "Svizzera + Regno Unito",  icon: MapPin,  flag: "🇬🇧" },
  { key: "US",       label: "Stati Uniti",             icon: MapPin,  flag: "🇺🇸" },
  { key: "ROW",      label: "Resto del Mondo",         icon: Globe,   flag: "🌍" },
];

const TIERS = [
  { key: "t1",  label: "1 – 3 pezzi",   max_qty: 3 },
  { key: "t2",  label: "4 – 6 pezzi",   max_qty: 6 },
  { key: "t3",  label: "7 – 10 pezzi",  max_qty: 10 },
  { key: "t4",  label: "11 pezzi e oltre", max_qty: null },
];

const EMPTY_ZONES = SHIPPING_ZONES.reduce((acc, z) => {
  acc[z.key] = TIERS.map((t) => ({ max_qty: t.max_qty, price: 0 }));
  return acc;
}, {});

export default function AdminPriceLists() {
  const { t } = useI18n();
  const [sp, setSp] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(sp.get("client") || "DEFAULT");
  const [pl, setPl] = useState(null);
  const [tab, setTab] = useState("products");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/admin/clients").then(({ data }) => setClients(data)); }, []);

  useEffect(() => {
    if (!clientId) return;
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      next.set("client", clientId);
      return next;
    });
    api.get(`/admin/pricelists/${clientId}`).then(({ data }) => {
      const safe = data?.items ? data : { client_id: clientId, name: "Default", items: [] };
      safe.shipping_zones = { ...EMPTY_ZONES, ...(safe.shipping_zones || {}) };
      safe.picking = safe.picking || { first: 1.22, additional: 0.61, cap: 12.20 };
      setPl(safe);
    });
  }, [clientId]);

  const save = async () => {
    setLoading(true);
    try {
      await api.put("/admin/pricelists", {
        client_id: pl.client_id,
        name: pl.name || "Default",
        items: pl.items,
        shipping_zones: pl.shipping_zones,
        picking: pl.picking,
      });
      toast.success(t("saved"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  const clearPrefill = () => {
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("new_code");
      return next;
    });
  };

  const clientLabel = clientId === "DEFAULT"
    ? "DEFAULT · Listino standard"
    : clients.find((c) => c.id === clientId)?.company_name;

  return (
    <div className="space-y-6" data-testid="admin-pricelists">
      <PageHeader
        eyebrow="Listini"
        title="Prezzi contrattuali"
        subtitle="Configura catalogo POD, zone di spedizione e commissioni picking per ogni cliente."
        chip="tf-chip-peach"
        icon={Receipt}
      />

      <div className="tf-card p-4">
        <label className="tf-label block mb-2">{t("choose_client")}</label>
        <select
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          className="tf-input max-w-md"
          data-testid="pl-client-select"
        >
          <option value="DEFAULT">DEFAULT · Listino standard</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
        </select>
      </div>

      {pl && (
        <div className="tf-card overflow-hidden">
          <div className="p-5 border-b border-[var(--tf-border)] flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="tf-label">{t("pricelist_for")}</div>
              <div className="font-display font-bold text-xl">{clientLabel}</div>
            </div>
            <button onClick={save} disabled={loading} className="tf-btn tf-btn-primary" data-testid="pl-save-btn">
              <FloppyDisk size={16} weight="bold" /> {t("save")}
            </button>
          </div>

          <div className="flex gap-1 px-5 pt-4 border-b border-[var(--tf-border)] bg-[var(--tf-bg)]/50">
            <TabButton active={tab === "products"} onClick={() => setTab("products")} icon={Package} label="Prodotti POD" testid="tab-products" />
            <TabButton active={tab === "shipping"} onClick={() => setTab("shipping")} icon={Truck} label="Spedizioni" testid="tab-shipping" />
            <TabButton active={tab === "premade"} onClick={() => setTab("premade")} icon={ShoppingBag} label="Premade · Picking" testid="tab-premade" />
          </div>

          <div className="p-5">
            {tab === "products" && (
              <ProductsSection pl={pl} setPl={setPl} prefillCode={sp.get("new_code") || ""} clearPrefill={clearPrefill} />
            )}
            {tab === "shipping" && <ShippingSection pl={pl} setPl={setPl} />}
            {tab === "premade"  && <PremadeSection pl={pl} setPl={setPl} />}
          </div>
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2.5 font-display font-bold text-sm border-b-2 transition-colors -mb-px ${
        active
          ? "border-[var(--tf-primary)] text-[var(--tf-ink)] bg-white rounded-t-lg"
          : "border-transparent text-[var(--tf-muted)] hover:text-[var(--tf-ink)]"
      }`}
    >
      <Icon size={16} weight="bold" />
      {label}
    </button>
  );
}

/* ---------- Products section (grouped by base product) ---------- */
const SLOT_ORDER = ["onelato", "twolati", "collo"];
const SLOT_SUFFIX = { onelato: "1side", twolati: "2sides", collo: "neck" };
const SLOT_LABEL = { onelato: "1 lato", twolati: "2 lati", collo: "Stampa collo" };

function slugify(str) {
  return str.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "prodotto";
}

function buildGroups(items) {
  const groups = {};
  const order = [];
  const fallback = [];
  (items || []).forEach((it) => {
    if (it.code === "default") { fallback.push(it); return; }
    const m = it.code.match(/^(.+)-(1side|2sides|neck)$/);
    const base = m ? m[1] : it.code;
    const slot = m ? (m[2] === "1side" ? "onelato" : m[2] === "2sides" ? "twolati" : "collo") : "onelato";
    if (!groups[base]) {
      const dashIdx = it.label.lastIndexOf(" - ");
      groups[base] = { base_code: base, base_label: dashIdx > -1 ? it.label.slice(0, dashIdx) : it.label, slots: {} };
      order.push(base);
    }
    groups[base].slots[slot] = { code: it.code, price: it.price, label: it.label };
  });
  return { order, groups, fallback };
}

function ProductsSection({ pl, setPl, prefillCode = "", clearPrefill }) {
  const [askDelete, setAskDelete] = useState(null);
  const [newLabel, setNewLabel] = useState("");
  const [newPrices, setNewPrices] = useState({ onelato: "", twolati: "", collo: "" });
  const [unknownName, setUnknownName] = useState("");
  const [unknownPrice, setUnknownPrice] = useState("");

  const { order, groups, fallback } = useMemo(() => buildGroups(pl.items), [pl.items]);
  const alreadyMapped = prefillCode && pl.items.some((it) => it.code === prefillCode);

  const parsePrice = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : null;
  };

  const setSlotPrice = (baseCode, slot, value) => {
    const group = groups[baseCode];
    const existing = group.slots[slot];
    let items = pl.items.slice();
    if (value === "") {
      if (existing) items = items.filter((it) => it.code !== existing.code);
    } else {
      const price = parsePrice(value);
      if (price === null) { toast.error("Prezzo non valido"); return; }
      const code = existing ? existing.code : `${baseCode}-${SLOT_SUFFIX[slot]}`;
      const idx = items.findIndex((it) => it.code === code);
      if (idx > -1) items[idx] = { ...items[idx], price };
      else items.push({ code, label: `${group.base_label} - ${SLOT_LABEL[slot]}`, price });
    }
    setPl({ ...pl, items });
  };

  const renameBase = (baseCode, newBaseLabel) => {
    const group = groups[baseCode];
    const items = pl.items.map((it) => {
      for (const slot of SLOT_ORDER) {
        if (group.slots[slot]?.code === it.code) {
          const oldLabel = group.slots[slot].label;
          const dashIdx = oldLabel.lastIndexOf(" - ");
          const descriptor = dashIdx > -1 ? oldLabel.slice(dashIdx + 3) : SLOT_LABEL[slot];
          return { ...it, label: `${newBaseLabel} - ${descriptor}` };
        }
      }
      return it;
    });
    setPl({ ...pl, items });
  };

  const deleteGroup = (baseCode) => {
    const group = groups[baseCode];
    const codes = new Set(SLOT_ORDER.map((s) => group.slots[s]?.code).filter(Boolean));
    setPl({ ...pl, items: pl.items.filter((it) => !codes.has(it.code)) });
  };

  const setFallbackPrice = (value) => {
    const price = parsePrice(value);
    if (price === null) { toast.error("Prezzo non valido"); return; }
    setPl({ ...pl, items: pl.items.map((it) => (it.code === "default" ? { ...it, price } : it)) });
  };

  const addNewGroup = () => {
    if (!newLabel.trim()) { toast.error("Inserisci il nome del prodotto"); return; }
    const baseCode = slugify(newLabel);
    if (groups[baseCode] || pl.items.some((it) => it.code === baseCode || it.code.startsWith(`${baseCode}-`))) {
      toast.error("Esiste già un prodotto con questo nome"); return;
    }
    const items = pl.items.slice();
    let added = 0;
    for (const slot of SLOT_ORDER) {
      const val = newPrices[slot];
      if (val === "" || val === undefined) continue;
      const price = parsePrice(val);
      if (price === null) { toast.error("Prezzo non valido"); return; }
      items.push({ code: `${baseCode}-${SLOT_SUFFIX[slot]}`, label: `${newLabel} - ${SLOT_LABEL[slot]}`, price });
      added += 1;
    }
    if (added === 0) { toast.error("Inserisci almeno un prezzo"); return; }
    setPl({ ...pl, items });
    setNewLabel("");
    setNewPrices({ onelato: "", twolati: "", collo: "" });
    toast.success("Prodotto creato");
  };

  const mapUnknownSku = () => {
    if (!unknownName.trim()) { toast.error("Inserisci il nome del prodotto"); return; }
    const price = parsePrice(unknownPrice);
    if (price === null) { toast.error("Inserisci un prezzo valido"); return; }
    const m = prefillCode.match(/^(.+)-(1side|2sides|neck)$/);
    const slot = m ? (m[2] === "1side" ? "onelato" : m[2] === "2sides" ? "twolati" : "collo") : "onelato";
    const items = pl.items.filter((it) => it.code !== prefillCode);
    items.push({ code: prefillCode, label: `${unknownName} - ${SLOT_LABEL[slot]}`, price });
    setPl({ ...pl, items });
    toast.success("SKU mappato. Clicca 'Salva' per sbloccare gli ordini in attesa.");
  };

  return (
    <div className="space-y-3">
      {prefillCode && !alreadyMapped && (
        <div className="tf-card p-4 border-2 border-[var(--tf-error)] bg-[#FBDDE1]/40" data-testid="unknown-sku-banner">
          <div className="flex items-center gap-2 font-display font-bold text-sm text-[var(--tf-error)]">
            <Warning size={16} weight="fill" /> SKU ignoto rilevato: <span className="font-mono">{prefillCode}</span>
          </div>
          <p className="text-xs text-[var(--tf-muted)] mt-1 mb-3">
            Assegna un nome e un prezzo per sbloccare gli ordini in attesa con questo codice.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-[2fr_120px_auto] gap-2">
            <input
              value={unknownName}
              onChange={(e) => setUnknownName(e.target.value)}
              placeholder="Nome prodotto"
              className="tf-input !py-1.5"
              data-testid="unknown-sku-name"
            />
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-[var(--tf-muted)] pointer-events-none">€</span>
              <input
                type="number" step="0.01"
                value={unknownPrice}
                onChange={(e) => setUnknownPrice(e.target.value)}
                placeholder="Prezzo"
                className="tf-input !py-1.5 !pl-6 text-right tf-numeric w-full"
                data-testid="unknown-sku-price"
              />
            </div>
            <button onClick={mapUnknownSku} className="tf-btn tf-btn-primary" data-testid="unknown-sku-map-btn">
              <Plus size={14} weight="bold" /> Mappa SKU
            </button>
          </div>
        </div>
      )}
      {prefillCode && alreadyMapped && (
        <div className="tf-card p-3 border border-[var(--tf-success)] bg-[#ECFCC9]/50 flex items-center justify-between gap-3" data-testid="unknown-sku-mapped">
          <span className="text-xs font-medium">SKU <span className="font-mono">{prefillCode}</span> mappato. Ricorda di cliccare "Salva".</span>
          <button onClick={clearPrefill} className="tf-btn tf-btn-ghost !py-1 !px-2 text-xs">Chiudi</button>
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="tf-label flex items-center gap-2"><Package size={12} weight="bold" /> Catalogo POD</div>
          <p className="text-xs text-[var(--tf-muted)] mt-1 max-w-2xl">
            1 lato / 2 lati / stampa collo. Lascia vuoto uno slot se la variante non è offerta. Prezzi <b>IVA esclusa</b>.
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display font-black text-xl tf-numeric">{order.length}</div>
          <div className="tf-label">prodotti</div>
        </div>
      </div>

      <div className="tf-card overflow-hidden">
        <div className="hidden sm:grid grid-cols-[1fr_100px_100px_100px_36px] gap-2 px-3 py-2 border-b border-[var(--tf-border)] bg-[var(--tf-bg)]/40 text-[10px] font-bold uppercase tracking-wide text-[var(--tf-muted)]">
          <div>Prodotto</div><div className="text-right">1 lato</div><div className="text-right">2 lati</div><div className="text-right">Collo</div><div></div>
        </div>
        {order.length === 0 && (
          <div className="p-6 text-center text-sm text-[var(--tf-muted)]">Nessun prodotto. Crea una voce qui sotto.</div>
        )}
        <div className="divide-y divide-[var(--tf-border)]">
          {order.map((baseCode) => {
            const group = groups[baseCode];
            return (
              <div
                key={baseCode}
                className="grid grid-cols-2 sm:grid-cols-[1fr_100px_100px_100px_36px] gap-2 items-center px-3 py-1.5 hover:bg-[var(--tf-bg)]/30"
                data-testid={`pl-group-${baseCode}`}
              >
                <input
                  value={group.base_label}
                  onChange={(e) => renameBase(baseCode, e.target.value)}
                  title={baseCode}
                  className="col-span-2 sm:col-span-1 font-medium text-sm bg-transparent border border-transparent hover:border-[var(--tf-border)] focus:border-[var(--tf-primary)] focus:outline-none rounded-md w-full truncate px-1.5 -mx-1.5 py-1 transition-colors"
                  data-testid={`pl-group-name-${baseCode}`}
                />
                {SLOT_ORDER.map((slot) => (
                  <div key={slot} className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-[var(--tf-muted)] pointer-events-none">€</span>
                    <input
                      type="number"
                      step="0.01"
                      value={group.slots[slot]?.price ?? ""}
                      placeholder="—"
                      onChange={(e) => setSlotPrice(baseCode, slot, e.target.value)}
                      className="tf-input !py-1 !pl-5 !text-sm text-right tf-numeric w-full"
                      data-testid={`pl-price-${baseCode}-${slot}`}
                    />
                  </div>
                ))}
                <button
                  onClick={() => setAskDelete({ baseCode, label: group.base_label })}
                  className="tf-btn tf-btn-danger !px-2 !py-1 shrink-0 justify-self-end"
                  data-testid={`pl-group-del-${baseCode}`}
                >
                  <Trash size={13} weight="bold" />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {fallback.length > 0 && (
        <div className="tf-card px-3 py-2 flex items-center gap-3 flex-wrap" data-testid="pl-fallback-row">
          <div className="flex-1 min-w-[200px]">
            <div className="text-xs font-medium">Fallback SKU non mappati</div>
            <div className="text-[11px] text-[var(--tf-muted)]">Usato quando un codice Myze non corrisponde a nessun prodotto.</div>
          </div>
          <div className="relative w-[100px]">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-[var(--tf-muted)] pointer-events-none">€</span>
            <input
              type="number"
              step="0.01"
              value={fallback[0].price}
              onChange={(e) => setFallbackPrice(e.target.value)}
              className="tf-input !py-1 !pl-5 !text-sm text-right tf-numeric w-full"
              data-testid="pl-fallback-price"
            />
          </div>
        </div>
      )}

      <div className="tf-card p-3 border-dashed">
        <div className="grid grid-cols-2 sm:grid-cols-[1fr_100px_100px_100px_auto] gap-2 items-center">
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Nuovo prodotto…"
            className="col-span-2 sm:col-span-1 tf-input !py-1.5 !text-sm"
            data-testid="pl-new-label"
          />
          {SLOT_ORDER.map((slot) => (
            <div key={slot} className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-[var(--tf-muted)] pointer-events-none">€</span>
              <input
                type="number"
                step="0.01"
                value={newPrices[slot]}
                onChange={(e) => setNewPrices({ ...newPrices, [slot]: e.target.value })}
                placeholder={SLOT_LABEL[slot]}
                className="tf-input !py-1.5 !pl-5 !text-sm text-right tf-numeric w-full"
                data-testid={`pl-new-price-${slot}`}
              />
            </div>
          ))}
          <button onClick={addNewGroup} className="tf-btn tf-btn-primary !py-1.5 justify-self-end" data-testid="pl-add-group-btn">
            <Plus size={14} weight="bold" /> Aggiungi
          </button>
        </div>
      </div>

      <DangerConfirm
        open={!!askDelete}
        onClose={() => setAskDelete(null)}
        onConfirm={() => { deleteGroup(askDelete.baseCode); setAskDelete(null); }}
        title="Eliminare questo prodotto dal listino?"
        subtitle={askDelete ? askDelete.label : ""}
        description="Tutte le varianti (1 lato / 2 lati / collo) di questo prodotto verranno rimosse dal listino cliente. L'azione è irreversibile ma le modifiche saranno definitive solo dopo aver cliccato 'Salva listino'."
        confirmLabel="Elimina prodotto"
        verifyWord="ELIMINA"
      />
    </div>
  );
}

/* ---------- Shipping section ---------- */
function ShippingSection({ pl, setPl }) {
  const updateTier = (zoneKey, tierIdx, price) => {
    const zones = { ...pl.shipping_zones };
    const arr = (zones[zoneKey] || []).slice();
    while (arr.length < TIERS.length) arr.push({ max_qty: TIERS[arr.length].max_qty, price: 0 });
    arr[tierIdx] = { max_qty: TIERS[tierIdx].max_qty, price: parseFloat(price) || 0 };
    zones[zoneKey] = arr;
    setPl({ ...pl, shipping_zones: zones });
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="tf-label flex items-center gap-2"><Truck size={12} weight="bold" /> Costi di spedizione</div>
        <p className="text-xs text-[var(--tf-muted)] mt-1.5 max-w-2xl">
          Tariffe a scaglione per zona geografica. Lo scaglione viene scelto in base al numero totale di pezzi nell&apos;ordine.
          IVA esclusa.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {SHIPPING_ZONES.map((z) => {
          const tiers = pl.shipping_zones[z.key] || EMPTY_ZONES[z.key];
          return (
            <div key={z.key} className="tf-card overflow-hidden" data-testid={`zone-${z.key}`}>
              <div className="p-4 border-b border-[var(--tf-border)] flex items-center gap-3 bg-[var(--tf-bg)]/40">
                <div className="text-2xl">{z.flag}</div>
                <div>
                  <div className="tf-label">Zona · {z.key}</div>
                  <div className="font-display font-bold text-base">{z.label}</div>
                </div>
              </div>
              <table className="tf-table">
                <thead>
                  <tr>
                    <th>Scaglione</th>
                    <th className="text-right">
                      <div>Prezzo €</div>
                      <div className="text-[9px] tracking-wider opacity-70 normal-case font-normal mt-0.5">IVA escl.</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {TIERS.map((tier, ti) => (
                    <tr key={tier.key}>
                      <td className="text-sm">{tier.label}</td>
                      <td className="text-right" style={{ width: 140 }}>
                        <input
                          type="number"
                          step="0.01"
                          value={tiers[ti]?.price ?? 0}
                          onChange={(e) => updateTier(z.key, ti, e.target.value)}
                          className="tf-input !py-1 text-right tf-numeric"
                          data-testid={`tier-${z.key}-${ti}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Premade / Picking section ---------- */
function PremadeSection({ pl, setPl }) {
  const p = pl.picking || { first: 1.22, additional: 0.61, cap: 12.20 };
  const setP = (key, val) => setPl({ ...pl, picking: { ...p, [key]: parseFloat(val) || 0 } });

  // Live preview: cost for 1, 3, 10, 25 premade items
  const calc = (qty) => {
    if (qty <= 0) return 0;
    return Math.min(p.first + (qty - 1) * p.additional, p.cap);
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="tf-label flex items-center gap-2"><ShoppingBag size={12} weight="bold" /> Gestione articoli premade</div>
        <p className="text-xs text-[var(--tf-muted)] mt-1.5 max-w-2xl">
          Costo di handling per gli articoli pre-confezionati (magneti, spille, portachiavi…) che non passano per la stampa POD.
          La formula è: <b>primo articolo</b> + (n-1) × <b>articolo aggiuntivo</b>, con un <b>tetto massimo</b> per ordine. IVA esclusa.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PickField label="Primo articolo" sublabel="IVA esclusa" value={p.first} onChange={(v) => setP("first", v)} icon={Hammer} testid="pick-first" />
        <PickField label="Articolo aggiuntivo" sublabel="Per pezzo dopo il primo" value={p.additional} onChange={(v) => setP("additional", v)} icon={Plus} testid="pick-additional" />
        <PickField label="Tetto massimo" sublabel="Limite per ordine" value={p.cap} onChange={(v) => setP("cap", v)} icon={House} testid="pick-cap" />
      </div>

      <div className="tf-card overflow-hidden">
        <div className="p-4 border-b border-[var(--tf-border)] bg-[var(--tf-bg)]/40">
          <div className="tf-label">Anteprima costo picking</div>
          <div className="text-xs text-[var(--tf-muted)] mt-1">Quanto verrà addebitato in base al numero di articoli premade nell&apos;ordine.</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[var(--tf-border)]">
          {[1, 3, 10, 25].map((qty) => (
            <div key={qty} className="p-4 text-center">
              <div className="tf-label">{qty} {qty === 1 ? "articolo" : "articoli"}</div>
              <div className="font-display font-black text-2xl mt-1.5 tf-numeric">€ {calc(qty).toFixed(2)}</div>
              {calc(qty) >= p.cap && qty > 1 && (
                <div className="text-[10px] text-[var(--tf-muted)] mt-1">tetto raggiunto</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PickField({ label, sublabel, value, onChange, icon: Icon, testid }) {
  return (
    <div className="tf-card p-4">
      <div className="flex items-center gap-2 text-[var(--tf-muted)]">
        <Icon size={16} weight="bold" />
        <div className="tf-label">{label}</div>
      </div>
      <div className="text-xs text-[var(--tf-muted)] mt-1">{sublabel}</div>
      <div className="flex items-center gap-2 mt-3">
        <span className="text-[var(--tf-muted)] font-display font-bold text-lg">€</span>
        <input
          type="number"
          step="0.01"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="tf-input tf-numeric text-lg !py-2"
          data-testid={testid}
        />
      </div>
    </div>
  );
}
